/**
 * NavigationHistoryContext - Android-style in-app back stack
 *
 * Tracks TrckIt-internal navigation using React Router's useNavigationType()
 * so the back button always knows whether there's a safe in-app page to return
 * to, instead of relying on the unreliable window.history.length / document.referrer.
 *
 * Stack behaviour:
 *   PUSH    → push new location onto stack
 *   REPLACE → swap top of stack with new location
 *   POP + empty stack → initial page load; push the entry
 *   POP + non-empty stack → user/programmatic back; pop the top
 *
 * canGoBack = stack.length > 1 (there is a prior in-app entry to return to)
 * goBack()  → navigate(-1) when canGoBack, else navigate('/', { replace: true })
 *
 * @atomic @idempotent
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

const NavigationHistoryContext = createContext({
  stack: [],
  canGoBack: false,
  goBack: () => {},
})

export function NavigationHistoryProvider({ children }) {
  const location = useLocation()
  const navigationType = useNavigationType() // 'POP' | 'PUSH' | 'REPLACE'
  const navigate = useNavigate()
  const [stack, setStack] = useState([])

  useEffect(() => {
    setStack(prev => {
      if (navigationType === 'PUSH') {
        return [...prev, location]
      }
      if (navigationType === 'REPLACE') {
        // Swap the top entry — replaces don't add depth
        return [...prev.slice(0, -1), location]
      }
      // POP: either the very first render (React Router always fires POP on mount)
      // or the user/code called navigate(-1).
      if (prev.length === 0) {
        // Initial load — seed the stack
        return [location]
      }
      // Genuine back navigation — pop the top
      return prev.slice(0, -1)
    })
  }, [location.key, navigationType]) // eslint-disable-line react-hooks/exhaustive-deps

  const canGoBack = stack.length > 1

  const goBack = useCallback(() => {
    if (canGoBack) {
      navigate(-1)
    } else {
      navigate('/', { replace: true })
    }
  }, [canGoBack, navigate])

  return (
    <NavigationHistoryContext.Provider value={{ stack, canGoBack, goBack }}>
      {children}
    </NavigationHistoryContext.Provider>
  )
}

export function useNavigationHistory() {
  return useContext(NavigationHistoryContext)
}
